/* eslint-disable @next/next/no-img-element */
"use client";
import { Icon } from "@components/blocks/icon/Icon";
import { useMapContext } from "@components/sections/mapSection/MapProvider";
import { Button } from "@components/ui/button";
import { useRoutes } from "@lib/utils/hooks/useRoutes";
import { MARKER_POPUP_OBJECT } from "@repo/map-helpers";
import { FacilitySummaryResponse } from "@repo/shared-types";
import { ArrowRight, X } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { Popup as MapboxPopup } from "react-map-gl";

export interface IMarkerPopup {
  categoryId: keyof typeof MARKER_POPUP_OBJECT;
  id: string;
  latitude: number;
  longitude: number;
  facility: FacilitySummaryResponse;
  navigationType?: "biking" | "hiking";
}

export function MarkerPopup({
  id,
  latitude,
  longitude,
  categoryId,
  facility,
}: IMarkerPopup) {
  const t = useTranslations();
  const routes = useRoutes();
  const searchParams = useSearchParams();
  const { navigate, navigationType, clearMarkerPopup, distance } =
    useMapContext();
  const type = searchParams.get("type");

  return (
    <>
      <MapboxPopup
        offset={20}
        key={id}
        latitude={latitude}
        longitude={longitude}
        closeOnClick={false}
        onClose={() => clearMarkerPopup()}
        className="mapboxgl-popup-marker absolute !z-50 h-[180px]"
      >
        <div className="items-top relative flex h-[152px] min-w-[300px] flex-row justify-start gap-3 pr-8">
          {facility?.image ? (
            <span className="relative min-h-[152px] min-w-[96px]">
              <img
                alt={facility.image.altText || "place image"}
                src={facility.image.url}
                onError={undefined}
                className="h-[152px] w-[96px] rounded-xl object-cover"
              />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
                <Icon
                  iconName={
                    MARKER_POPUP_OBJECT[categoryId] ?? "markerFallbackWhite"
                  }
                />
              </span>
            </span>
          ) : (
            <div className="grid h-[152px] min-h-[152px] w-[96px] min-w-[96px] items-center justify-center rounded-xl bg-[#DCE8CA]">
              <Icon iconName="logoMarker" />
            </div>
          )}
          <Button
            size="xs"
            className="absolute right-0 top-0 rounded-full"
            onClick={() => clearMarkerPopup()}
          >
            <X className="h-5 w-5" />
          </Button>
          <div className="items-top flex max-w-[160px] flex-col">
            <p className="truncate text-base font-bold">{facility.name}</p>
            <p className="text-xs font-light">{facility?.address?.street}</p>
            <Link
              href={routes.facility.route({ id: facility.id })}
              className="mt-2 flex items-center gap-1 text-balance border-accent text-sm font-medium text-accent ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-inactive disabled:text-inactive-foreground"
              target="_blank"
            >
              {t("Filters.Paths.ReadMore")}
              <ArrowRight className="h-[14px] w-[14px]" />
            </Link>
            <div className="mt-auto flex flex-col space-y-1">
              <span className="block font-bold">
                {t("Filters.Paths.DrivingDirections")}
              </span>
              <span className="mt-auto flex w-[195px] items-center space-x-2">
                <button
                  onClick={async () => {
                    await navigate(longitude, latitude, "hiking");
                  }}
                  className={`group ${type === "biking" ? "hidden" : "-ml-1 flex"} items-center space-x-1 p-1 text-sm text-accent`}
                >
                  <span
                    className={`grid h-6 w-6 place-items-center rounded-full border border-black p-1 ${navigationType === "hiking" ? "bg-[#F9C026]" : "bg-transparent"} group-hover:bg-[#F9C026]`}
                  >
                    <Icon iconName="walk" />
                  </span>
                  <span className="block group-hover:underline">
                    {t("Filters.Hiking")}
                  </span>
                </button>
                <button
                  onClick={async () => {
                    await navigate(longitude, latitude, "biking");
                  }}
                  className={`group ${type === "hiking" ? "hidden" : "flex"} ${type === "biking" ? "!-ml-1" : ""}  items-center space-x-1 p-1 text-sm text-accent`}
                >
                  <span
                    className={`grid h-6 w-6 place-items-center rounded-full border border-black p-1 ${navigationType === "biking" ? "bg-[#FA6732]" : "bg-transparent"} group-hover:bg-[#FA6732]`}
                  >
                    <Icon iconName="bike" />
                  </span>
                  <span className="block group-hover:underline">
                    {t("Filters.Biking")}
                  </span>
                </button>
              </span>
              {distance ? (
                <p className="text-xs font-light">
                  {distance} {t("Map.DistanceIndicator")}
                </p>
              ) : (
                <div className="h-[1rem] text-xs font-light" />
              )}
            </div>
          </div>
        </div>
      </MapboxPopup>
    </>
  );
}
